import { ROUTES } from '@repo/shared-types/routeRegistry';

/**
 * Single source of truth for deriving user identity from onboarding stepData by role.
 * Used so consumers (LandingView, completeClerkOnboarding payload) don't branch on step keys.
 */

/** Return the post-onboarding destination route for a role. Investor gets thank-you (no portal yet). */
export function getPostOnboardingRoute(role: string, isSelfManagingOwner?: boolean): string {
  switch (role) {
    case 'OWNER':
      return ROUTES.OWNER_DASHBOARD;
    case 'PROPERTY_MANAGER':
      return isSelfManagingOwner ? ROUTES.OWNER_STATEMENTS : ROUTES.APP;
    case 'VENDOR':
      return ROUTES.VENDOR_JOBS;
    case 'INVESTOR':
      return `${ROUTES.ROOT}?investor=thankyou`;
    case 'TENANT':
      return ROUTES.APP;
    default:
      return ROUTES.APP;
  }
}

export interface OnboardingIdentity {
  firstName: string;
  lastName: string;
  email: string | undefined;
}

/**
 * Extract identity (firstName, lastName, email) from wizard stepData for the given role.
 * Owner uses owner-profile; Vendor uses business (contactFirstName, contactLastName, email);
 * PM, Tenant, Investor use personal or entity.
 */
export function getOnboardingIdentity(
  role: string,
  stepData: Record<string, unknown> | undefined
): OnboardingIdentity {
  const personal = (stepData?.personal ?? stepData?.entity) as Record<string, unknown> | undefined;
  let firstName = typeof personal?.firstName === 'string' ? personal?.firstName.trim() ?? '' : '';
  let lastName = typeof personal?.lastName === 'string' ? personal?.lastName.trim() ?? '' : '';
  let email = typeof personal?.email === 'string' ? personal?.email.trim() : undefined;

  if (role === 'OWNER') {
    const ownerProfile = stepData?.['owner-profile'] as Record<string, unknown> | undefined;
    if (ownerProfile) {
      if (typeof ownerProfile.firstName === 'string' && ownerProfile.firstName.trim())
        firstName = ownerProfile.firstName.trim();
      if (typeof ownerProfile.lastName === 'string' && ownerProfile.lastName.trim())
        lastName = ownerProfile.lastName.trim();
      if (typeof ownerProfile.email === 'string' && ownerProfile.email.trim())
        email = ownerProfile.email.trim();
    }
  } else if (role === 'VENDOR') {
    const business = stepData?.business as Record<string, unknown> | undefined;
    if (business) {
      const contactFirst = business.contactFirstName;
      const contactLast = business.contactLastName;
      if (typeof contactFirst === 'string' && contactFirst.trim()) firstName = contactFirst.trim();
      if (typeof contactLast === 'string' && contactLast.trim()) lastName = contactLast.trim();
      if (typeof business.email === 'string' && business.email.trim()) email = business.email.trim();
    }
  }

  return { firstName, lastName, email };
}
